import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Input,
  Row,
  Col,
  message,
  Typography,
  Statistic,
  Progress,
  Badge,
  Timeline,
  InputNumber,
  Popconfirm,
  Table,
  Tooltip
} from 'antd';
import {
  ScanOutlined,
  BarcodeOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  ClockCircleOutlined,
  InboxOutlined,
  WarningOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import moment from 'moment';
import type { 
  PackingOrder,
  PackingOrderItem,
  PackingScanResult
} from '../../types/packing';
import apiService from '../../../../services/apiService';
import { printWaybillSilently, printHktvMallWaybillSilently } from '@utils/pdfUtils.ts';
import { playErrorSound } from '../../../../utils/soundUtils';

const { Title, Text } = Typography;

interface PackingScanInterfaceProps {
  packingOrder: PackingOrder | null;
  onUpdatePackingOrder: (updated: PackingOrder) => void;
  onClose?: () => void;
}

const PackingScanInterface: React.FC<PackingScanInterfaceProps> = ({
  packingOrder,
  onUpdatePackingOrder,
  onClose
}) => {
  // mark prop as read to satisfy linter
  void onClose;
  // 创建空订单作为默认值
  const emptyOrder: PackingOrder = {
    id: '',
    outboundOrderId: '',
    orderNumber: '',
    waybillId: '',
    waybillNumber: '',
    status: 'pending',
    packingZoneId: '',
    packingZoneName: '',
    pickupZoneId: '',
    pickupZoneName: '',
    packageType: '',
    packageWeight: 0,
    packageDimensions: '',
    packageValue: 0,
    items: [],
    totalItems: 0,
    totalQuantity: 0,
    customerName: '',
    customerContact: '',
    deliveryAddress: '',
    receiverName: '',
    receiverContact: '',
    discrepancies: [],
    createdAt: new Date(),
    updatedAt: new Date(),
  };
  
  const [currentOrder, setCurrentOrder] = useState<PackingOrder>(packingOrder || emptyOrder);
  const [scanMode, setScanMode] = useState<'waybill' | 'packing' | 'verification'>('waybill');
  const [scanning, setScanning] = useState(true);
  const [, setCurrentItem] = useState<PackingOrderItem | null>(null);
  const [scanHistory, setScanHistory] = useState<PackingScanResult[]>([]);
  
  
  // Scanner detection
  const [scanBuffer, setScanBuffer] = useState('');
  const [lastScanTime, setLastScanTime] = useState(0);
  const [isRealScannerMode, setIsRealScannerMode] = useState(false);
  
  const scanInputRef = useRef<any>(null);
  // 移除未使用的表單實例
  // 統一的掃描輸入值
  const [scanInputValue, setScanInputValue] = useState('');
  // 避免重複觸發 PDF 下載
  const [pdfDownloaded, setPdfDownloaded] = useState(false);
  // 與揀貨一致的條碼/SKU歸一化
  const normalize = (v?: string | null) => String(v ?? '').trim();
  /*const normalize = (v?: string | null) =>
    String(v ?? '')
      .replace(/\s+/g, '')
      .replace(/[\u0000-\u001F\u007F]/g, '')
      .replace(/[^0-9A-Za-z]/g, '');*/

  // 一次性：當所有產品已掃描=預期時，將出庫單狀態設為「已包裝」（PACKED）
  const didMarkPackedRef = useRef<boolean>(false);
  const ensureOutboundPackedOnce = async (orderNumber?: string) => {
    if (didMarkPackedRef.current) return;
    const num = String(orderNumber || currentOrder.orderNumber || '').trim();
    if (!num) { didMarkPackedRef.current = true; return; }
    try {
      await apiService.updateOutboundOrder(num, { status: 'PACKED' });
    } catch {
      // 忽略錯誤，不影響畫面流程
    } finally {
      didMarkPackedRef.current = true;
    }
  };

  // 將後端錯誤轉換為友善中文文案
  const getFriendlyErrorMessage = (err: any, context: 'waybill' | 'product' | 'verification'): string => {
    const raw = err?.response?.data?.message || err?.message || String(err ?? '');
    const lower = String(raw).toLowerCase();

    if (context === 'waybill') {
      const is404 = err?.response?.status === 404 || lower.includes('404');
      const mentionsNotExist = lower.includes('not exist') || lower.includes('not found');
      const mentionsWaybill = lower.includes('waybill');
      if (is404 || (mentionsWaybill && mentionsNotExist)) {
        return '未找到此運單的產品';
      }
    }

    return raw || '系統錯誤';
  };

  // 自動打印並生成 PDF（僅在未下載過時觸發）
  // 使用靜默打印，不打開新視窗
  const attemptGeneratePdf = async (order: PackingOrder) => {
    const orderNo = order.orderNumber
    const waybillNo = order.waybillNumber;
    if (!waybillNo || !orderNo || pdfDownloaded) return;

    try {
      if(waybillNo) {
        if (String(orderNo).includes('H')) {
          await printHktvMallWaybillSilently(orderNo, waybillNo);
        } else {
          await printWaybillSilently(orderNo, waybillNo);
        }
      }

      setPdfDownloaded(true);
    } catch (pdfError) {
      console.error('打印和PDF生成失敗:', pdfError);
      // Error message is already handled by the silent print functions
    }
  };

  const handleClearScanHistory = () => {
    setScanHistory([]);
    message.success('已清空掃描歷史');
  };

  useEffect(() => {
    // Focus on scan input when component mounts or scan mode changes
    if (scanInputRef.current) {
      scanInputRef.current.focus();
    }
  }, [scanMode]);

  // Hardware barcode scanner integration
  useEffect(() => {
    let scannerTimeout: NodeJS.Timeout;
    
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is typing in input fields
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
        return;
      }

      const currentTime = Date.now();
      
      // Check if this could be from a barcode scanner (rapid input)
      if (currentTime - lastScanTime < 100) {
        setIsRealScannerMode(true);
      }
      
      setLastScanTime(currentTime);

      // Handle Enter key (scanner suffix)
      if (event.key === 'Enter') {
        event.preventDefault();
        if (scanBuffer.trim().length > 0) {
          handleScan(scanBuffer.trim());
          setScanBuffer('');
          setIsRealScannerMode(false);
        }
        return;
      }

      // Handle regular characters (build barcode)
      if (event.key.length === 1) {
        event.preventDefault();
        setScanBuffer(prev => prev + event.key);
        
        // Clear buffer after delay (in case scanner doesn't send Enter)
        clearTimeout(scannerTimeout);
        scannerTimeout = setTimeout(() => {
          if (scanBuffer.length > 8) { // Minimum barcode length
            handleScan(scanBuffer.trim());
          }
          setScanBuffer('');
          setIsRealScannerMode(false);
        }, 300);
      }
    };

    // Add global keyboard listener for barcode scanner
    if (scanning) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(scannerTimeout);
    };
  }, [scanning, scanBuffer, lastScanTime]);

  // Visual feedback for scanner detection
  useEffect(() => {
    if (isRealScannerMode && scanBuffer.length > 0) {
      // Show visual feedback that scanner is detected
      const indicator = document.createElement('div');
      indicator.style.cssText = `
        position: fixed;
        top: 20px;
        right: 20px;
        background: #52c41a;
        color: white;
        padding: 8px 16px;
        border-radius: 4px;
        z-index: 9999;
        font-size: 14px;
        box-shadow: 0 2px 8px rgba(0,0,0,0.15);
      `;
      indicator.textContent = `🔍 掃描中... ${scanBuffer}`;
      document.body.appendChild(indicator);
      
      setTimeout(() => {
        if (document.body.contains(indicator)) {
          document.body.removeChild(indicator);
        }
      }, 2000);
    }
  }, [isRealScannerMode, scanBuffer]);

  const handleWaybillScan = async (barcode: string) => {
    try {
      const searchTerm = barcode.trim();
      
      // 清空之前的掃描歷史，開始新的包裝訂單
      setScanHistory([]);
      
      // 第一步：尝试从包装订单列表中查找
      /*try {
        const packingResp: any = await apiService.getPackingOrders({ search: searchTerm, limit: 10 });
        const packingOrders = packingResp?.data || packingResp?.packings || [];
        
        // 查找匹配的订单（运单号或订单号）
        const matchedOrder = packingOrders.find((po: any) => 
          po.waybillNumber === searchTerm || 
          po.orderNumber === searchTerm ||
          po.packingNumber === searchTerm
        );
        
        if (matchedOrder) {
          // 找到已存在的包装订单，加载详情
          const detailResp: any = await apiService.getPackingOrderDetail(matchedOrder.id);
          const orderDetail = detailResp?.data || detailResp;
          
          if (orderDetail) {
            // 转换为 PackingOrder 格式
            const packingOrder: PackingOrder = {
              id: orderDetail.id,
              outboundOrderId: orderDetail.outboundOrderId || orderDetail.outbound_order_id || '',
              orderNumber: orderDetail.orderNumber || orderDetail.order_number || orderDetail.packingNumber || orderDetail.packing_number || '',
              waybillId: orderDetail.waybillId || orderDetail.waybill_id || '',
              waybillNumber: orderDetail.waybillNumber || orderDetail.waybill_number || searchTerm,
              items: (orderDetail.items || []).map((item: any) => ({
                id: item.id,
                productId: item.productId || item.product_id,
                productSku: item.productSku || item.product_sku,
                productName: item.productName || item.product_name,
                productBarcode: item.productBarcode || item.product_barcode || item.productSku || item.product_sku,
                expectedQuantity: item.expectedQuantity || item.expected_quantity || item.quantity,
                scannedQuantity: item.scannedQuantity || item.scanned_quantity || 0,
                packedQuantity: item.packedQuantity || item.packed_quantity || 0,
                currentLocation: item.currentLocation || item.current_location,
                targetLocation: item.targetLocation || item.target_location,
                status: item.status || 'pending',
                hasDiscrepancy: item.hasDiscrepancy || item.has_discrepancy || false,
                lotNumber: item.lotNumber || item.lot_number,
                serialNumber: item.serialNumber || item.serial_number,
              })),
              status: orderDetail.status || 'packing',
              packingZoneId: orderDetail.packingZoneId || orderDetail.packing_zone_id || '',
              packingZoneName: orderDetail.packingZoneName || orderDetail.packing_zone_name || '',
              pickupZoneId: orderDetail.pickupZoneId || orderDetail.pickup_zone_id || '',
              pickupZoneName: orderDetail.pickupZoneName || orderDetail.pickup_zone_name || '',
              packageType: orderDetail.packageType || orderDetail.package_type || '',
              packageWeight: orderDetail.packageWeight || orderDetail.package_weight || 0,
              packageDimensions: orderDetail.packageDimensions || orderDetail.package_dimensions || '',
              packageValue: orderDetail.packageValue || orderDetail.package_value || 0,
              totalItems: orderDetail.totalItems || orderDetail.total_items || (orderDetail.items || []).length,
              totalQuantity: orderDetail.totalQuantity || orderDetail.total_quantity || 0,
              customerName: orderDetail.customerName || orderDetail.customer_name || '',
              customerContact: orderDetail.customerContact || orderDetail.customer_contact || '',
              receiverName: orderDetail.receiverName || orderDetail.receiver_name || '',
              receiverContact: orderDetail.receiverContact || orderDetail.receiver_contact || '',
              deliveryAddress: orderDetail.deliveryAddress || orderDetail.delivery_address || '',
              discrepancies: orderDetail.discrepancies || [],
              createdAt: orderDetail.createdAt || orderDetail.created_at || new Date(),
              updatedAt: new Date(),
            };
            
            const scanResult: PackingScanResult = {
              barcode: searchTerm,
              type: 'waybill',
              success: true,
              data: packingOrder,
              timestamp: new Date(),
              operatorId: 'current_user',
              operatorName: 'Current User'
            };
            setScanHistory([scanResult]);

            setCurrentOrder(packingOrder);
            onUpdatePackingOrder(packingOrder);
            setScanMode('packing');
            message.success(`已載入包裝訂單: ${packingOrder.orderNumber}`);
            return;
          }
        }
      } catch (packingErr) {
        console.log('未找到包装订单，尝试从 HKTV 获取...', packingErr);
      }*/
      
      // 第二步：如果没找到包装订单，尝试从 HKTV 加载（保留原有逻辑）
      const resp: any = await apiService.getHKTVOrderByTrackingId(searchTerm);
      const order = resp?.data || resp;
      if (!order) {
        throw new Error('找不到對應訂單');
      }
      console.log('HKTV 訂單:====================', order);

      // 將 HKTV 訂單條目映射為包裝項目列表
      const items = Array.isArray(order.consignmentEntries) ? order.consignmentEntries.map((e: any, idx: number) => ({
        id: String(e.id || idx + 1),
        productId: e.skuId,
        productSku: e.skuId,
        productName: e.skuNameZh,
        productBarcode: e.productBarcode,
        expectedQuantity: Number(e.quantity || 1),
        scannedQuantity: 0,
        packedQuantity: 0,
        currentLocation: currentOrder.packingZoneId,
        targetLocation: currentOrder.packingZoneId,
        status: 'pending' as const,
        hasDiscrepancy: false,
      })) : [];

      const updatedOrder = {
        ...currentOrder,
        waybillNumber: searchTerm,
        orderNumber: order.orderId || currentOrder.orderNumber,
        receiverName: order.recipientName || currentOrder.receiverName,
        deliveryAddress: order.deliveryAddress || currentOrder.deliveryAddress,
        items,
        status: 'packing' as const,
        updatedAt: new Date(),
      };

      const scanResult: PackingScanResult = {
        barcode: searchTerm,
        type: 'waybill',
        success: true,
        data: updatedOrder,
        timestamp: new Date(),
        operatorId: 'current_user',
        operatorName: 'Current User'
      };
      setScanHistory([scanResult]);

      setCurrentOrder(updatedOrder);
      onUpdatePackingOrder(updatedOrder);
      setScanMode('packing');
      message.success('已載入 HKTV 訂單與產品');
    } catch (err: any) {
      const friendly = getFriendlyErrorMessage(err, 'waybill');
      const scanResult: PackingScanResult = {
        barcode,
        type: 'waybill',
        success: false,
        error: friendly,
        timestamp: new Date(),
        operatorId: 'current_user',
        operatorName: 'Current User'
      };
      setScanHistory([scanResult]);
      playErrorSound();
      message.error(`掃描失敗：${scanResult.error}`);
    }
  };

  const handleProductScan = async (barcode: string) => {
    const norm = normalize(barcode);
    // Find the product in the order items（歸一化匹配）
    const item = currentOrder.items.find(it => {
      const barcodes = (it.productBarcode || '').split(',');
      return barcodes.includes(norm);     // 任一條碼匹配即可
    });
    /*const item = currentOrder.items.find(it =>
      normalize(it.productBarcode) === norm ||
      normalize(it.productSku) === norm ||
      (!!it.lotNumber && normalize(it.lotNumber) === norm) ||
      (!!it.serialNumber && normalize(it.serialNumber) === norm)
    );*/

    if (!item) {
      const scanResult: PackingScanResult = {
        barcode,
        type: 'product',
        success: false,
        error: '未找到對應產品',
        timestamp: new Date(),
        operatorId: 'current_user',
        operatorName: 'Current User'
      };
      setScanHistory([scanResult, ...scanHistory]);
      playErrorSound();
      message.error('未找到對應產品');
      return;
    }

    // Check if item is already fully scanned（期望為0時，上限視為1）
    const capForDone = Math.max(1, item.expectedQuantity || 0);
    if ((item.scannedQuantity || 0) >= capForDone) {
      message.warning('該產品已完成掃描');
      return;
    }

    // Successful product scan
    const scanResult: PackingScanResult = {
      barcode,
      type: 'product',
      success: true,
      data: item,
      timestamp: new Date(),
      operatorId: 'current_user',
      operatorName: 'Current User'
    };

    setScanHistory([scanResult, ...scanHistory]);
    setCurrentItem(item);
    
    // Auto-increment scanned quantity by 1
    const updatedItems = currentOrder.items.map(i => {
      if (i.id === item.id) {
        const cap = Math.max(1, i.expectedQuantity || 0);
        const newScannedQty = Math.min((i.scannedQuantity || 0) + 1, cap);
        return {
          ...i,
          scannedQuantity: newScannedQty,
          status: newScannedQty >= (i.expectedQuantity || 0) && (i.expectedQuantity || 0) > 0 ? 'scanned' as const : 'pending' as const,
          scannedAt: new Date(),
          scannedBy: 'current_user'
        };
      }
      return i;
    });

    const updatedOrder = {
      ...currentOrder,
      items: updatedItems,
      updatedAt: new Date()
    };

    setCurrentOrder(updatedOrder);
    onUpdatePackingOrder(updatedOrder);
    
    message.success(`${item.productName} 掃描 +1`);
    
    // 若所有項目「預期 = 已掃描」，一次性：先更新出庫狀態為 PACKED，再自動生成 PDF（各一次）
    try {
      const allScannedFinished = updatedItems.length > 0 && updatedItems.every(it => {
        const expected = Math.max(1, Number(it.expectedQuantity || 0));
        const scanned = Number(it.scannedQuantity || 0);
        return scanned === expected;
      });
      if (allScannedFinished) {
        // 更新出庫訂單狀態
        await ensureOutboundPackedOnce(currentOrder.orderNumber);
        
        // 更新包裝訂單狀態為 packed
        const hadExistingPackingOrder = Boolean(currentOrder.id);
        // 本地同步：將各項目的 packedQuantity 至少等於已掃描數，並更新訂單狀態
        const finalizedItems = updatedItems.map(it => ({
          ...it,
          packedQuantity: Math.max(Number(it.packedQuantity || 0), Number(it.scannedQuantity || 0))
        }));

        let packingOrderId = currentOrder.id;

        if (!packingOrderId) {
          try {
            const payload = {
              outboundOrderId: currentOrder.outboundOrderId || null,
              orderNumber: currentOrder.orderNumber || null,
              waybillId: currentOrder.waybillId || null,
              waybillNumber: currentOrder.waybillNumber || null,
              trackingNumber: currentOrder.trackingNumber || currentOrder.waybillNumber || null,
              status: 'packed',
              packingZoneId: currentOrder.packingZoneId || null,
              packingZoneName: currentOrder.packingZoneName || null,
              pickupZoneId: currentOrder.pickupZoneId || null,
              pickupZoneName: currentOrder.pickupZoneName || null,
              customerName: currentOrder.customerName || currentOrder.receiverName || '',
              customerContact: currentOrder.customerContact || currentOrder.receiverContact || '',
              deliveryAddress: currentOrder.deliveryAddress || '',
              receiverName: currentOrder.receiverName || '',
              receiverContact: currentOrder.receiverContact || '',
              deliveryCompany: currentOrder.deliveryCompany || null,
              packageType: currentOrder.packageType || null,
              packageWeight: currentOrder.packageWeight ?? null,
              packageDimensions: currentOrder.packageDimensions || null,
              packageValue: currentOrder.packageValue ?? null,
              items: finalizedItems.map(it => ({
                id: it.id,
                productSku: it.productSku,
                productName: it.productName,
                productBarcode: it.productBarcode,
                expectedQuantity: Number(it.expectedQuantity || 0),
                scannedQuantity: Number(it.scannedQuantity || 0),
                packedQuantity: Number(it.packedQuantity || 0),
                lotNumber: it.lotNumber || null,
                serialNumber: it.serialNumber || null,
                status: it.status || 'packed'
              }))
            };
            const createResp: any = await apiService.createPackingOrder(payload);
            packingOrderId = createResp?.id || createResp?.data?.id || packingOrderId;
            if (!packingOrderId) {
              console.warn('[PackingScan] Packing order created but no id returned', createResp);
            }
          } catch (createErr) {
            console.error('[PackingScan] Failed to create packing order:', createErr);
          }
        }

        if (packingOrderId && hadExistingPackingOrder) {
          try {
            await apiService.markPackingPacked(packingOrderId);
            console.log('[PackingScan] Marked packing order as packed:', packingOrderId);
          } catch (err) {
            console.error('[PackingScan] Failed to mark packing order as packed:', err);
          }
        }

        const orderAfterPack = {
          ...updatedOrder,
          id: packingOrderId || updatedOrder.id,
          items: finalizedItems,
          status: 'packed' as const,
          updatedAt: new Date()
        };
        setCurrentOrder(orderAfterPack);
        onUpdatePackingOrder(orderAfterPack);
        
        // 生成 PDF
        if (!pdfDownloaded) {
          try {
            console.log('[PackingScan] 開始生成運單PDF...');
            await attemptGeneratePdf(currentOrder);
            console.log('[PackingScan] 運單PDF生成完成');
          } catch (pdfError) {
            console.error('[PackingScan] PDF生成失敗，但包裝流程已完成:', pdfError);
            message.warning('包裝完成，但PDF生成失敗，請手動打印運單');
          }
        }

        // 自動重置到乾淨的包裝頁面（延遲執行以便打印完成和用戶看到訊息）
        // 增加延遲到 3 秒，確保打印任務有足夠時間完成
        message.success('包裝完成！正在打印運單...', 2);
        setTimeout(() => {
          // 重置所有狀態到初始值
          setScanHistory([]);
          setCurrentOrder(emptyOrder);
          setScanMode('waybill');
          setPdfDownloaded(false);
          setScanInputValue('');
          setCurrentItem(null);
          setScanBuffer('');
          setLastScanTime(0);
          didMarkPackedRef.current = false;

          // 通知父組件
          onUpdatePackingOrder(emptyOrder);

          message.info('已重置包裝頁面，可以開始新的包裝', 3);
        }, 3000);  // 增加從 1500ms 到 3000ms
      }
    } catch (error) {
      // 添加錯誤日誌以幫助調試
      console.error('[PackingScan] 包裝流程錯誤:', error);
    }

    // 以上邏輯已在全部掃描完成時同步 packedQuantity，確保進度條即時反映
  };

  const handleScan = async (barcode: string) => {
    if (!barcode || !barcode.trim()) return;
    
    setScanning(true);
    
    try {
      // 自動判斷：如果當前訂單沒有運單號或訂單為空，優先當作運單號
      if (!currentOrder.waybillNumber || currentOrder.items.length === 0) {
        await handleWaybillScan(barcode.trim());
      } else {
        // 檢查是否掃描的是運單號，如果是，則完成所有產品掃描
        if (barcode.trim() === currentOrder.waybillNumber) {
          // 完成所有產品掃描
          const updatedItems = currentOrder.items.map(item => {
            const expected = Math.max(1, item.expectedQuantity || 0);
            return {
              ...item,
              scannedQuantity: expected,
              packedQuantity: expected,
              status: 'scanned' as const
            };
          });

          const updatedOrder = {
            ...currentOrder,
            items: updatedItems,
            status: 'packed' as const,
            updatedAt: new Date()
          };

          setCurrentOrder(updatedOrder);
          onUpdatePackingOrder(updatedOrder);
          
          // 觸發完成流程
          await ensureOutboundPackedOnce(currentOrder.orderNumber);
          await attemptGeneratePdf(currentOrder);
          
          message.success('包裝完成！所有產品已標記為已掃描和已包裝。');
          
          // 自動重置到乾淨的包裝頁面（延遲執行以便用戶看到完成訊息）
          setTimeout(() => {
            // 重置所有狀態到初始值
            setScanHistory([]);
            setCurrentOrder(emptyOrder);
            setScanMode('waybill');
            setPdfDownloaded(false);
            setScanInputValue('');
            setCurrentItem(null);
            setScanBuffer('');
            setLastScanTime(0);
            didMarkPackedRef.current = false;

            // 通知父組件
            onUpdatePackingOrder(emptyOrder);

            message.info('已重置包裝頁面，可以開始新的包裝', 3);
          }, 1500);
        } else {
          // 否則當作產品條碼
          await handleProductScan(barcode.trim());
        }
      }
    } finally {
      setScanning(false);
    }
  };

  const handleManualQuantityUpdate = (itemId: string, type: 'scanned' | 'packed', newQuantity: number) => {
    const updatedItems = currentOrder.items.map(item => {
      if (item.id === itemId) {
        const updates: Partial<PackingOrderItem> = {};
        
        if (type === 'scanned') {
          updates.scannedQuantity = Math.max(0, Math.min(newQuantity, item.expectedQuantity));
        } else {
          updates.packedQuantity = Math.max(0, Math.min(newQuantity, item.scannedQuantity));
        }
        
        return { ...item, ...updates };
      }
      return item;
    });

    const updatedOrder = {
      ...currentOrder,
      items: updatedItems,
      updatedAt: new Date()
    };

    setCurrentOrder(updatedOrder);
    onUpdatePackingOrder(updatedOrder);
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'pending': 'default',
      'packing': 'blue',
      'packed': 'green'
    };
    return colors[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const texts: Record<string, string> = {
      'pending': '待處理',
      'packing': '已包裝',
      'packed': '已打包'
    };
    return texts[status] || status;
  };

  const totalQuantity = currentOrder.items.reduce((sum, item) => sum + item.expectedQuantity, 0);
  const scannedQuantity = currentOrder.items.reduce((sum, item) => sum + item.scannedQuantity, 0);
  const packedQuantity = currentOrder.items.reduce((sum, item) => sum + item.packedQuantity, 0);

  return (
    <div style={{ padding: '24px' }}>
      <style>
        {`
          @keyframes pulse {
            0% { opacity: 1; }
            50% { opacity: 0.5; }
            100% { opacity: 1; }
          }
        `}
      </style>
      
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2}>
          <ScanOutlined /> 包裝掃描操作
        </Title>
        <Text type="secondary">
          訂單編號: {currentOrder.orderNumber} | 配貨單: {currentOrder.waybillNumber}
        </Text>
      </div>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: '24px' }}>
        <Col span={6}>
          <Card>
            <Statistic 
              title="總數量" 
              value={totalQuantity}
              prefix={<InboxOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="已掃描" 
              value={scannedQuantity}
              valueStyle={{ color: '#722ed1' }}
              prefix={<ScanOutlined />}
            />
            <Progress 
              percent={totalQuantity > 0 ? Math.round((scannedQuantity / totalQuantity) * 100) : 0}
              size="small"
              status="active"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="已包裝" 
              value={packedQuantity}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
            <Progress 
              percent={totalQuantity > 0 ? Math.round((packedQuantity / totalQuantity) * 100) : 0}
              size="small"
              status="success"
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic 
              title="差異項目" 
              value={currentOrder.items.filter(item => item.hasDiscrepancy).length}
              valueStyle={{ color: currentOrder.items.some(item => item.hasDiscrepancy) ? '#ff4d4f' : '#52c41a' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Row gutter={16}>
        <Col span={16}>
          {/* Scan Input */}
          <Card
            title={
              <Space>
                <ScanOutlined />
                <span>掃描區域</span>
                <Tag color={getStatusColor(currentOrder.status)}>
                  {getStatusText(currentOrder.status)}
                </Tag>
              </Space>
            }
            style={{ marginBottom: '16px' }}
          >
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <BarcodeOutlined 
                style={{ 
                  fontSize: '64px', 
                  color: scanning ? '#1890ff' : '#ccc',
                  marginBottom: '16px'
                }} 
              />
              <div style={{ marginBottom: '24px' }}>
                <Input
                  ref={scanInputRef}
                  placeholder="掃描條碼..."
                  size="large"
                  prefix={<ScanOutlined />}
                  value={scanInputValue}
                  onChange={(e) => setScanInputValue(e.target.value)}
                  onPressEnter={() => {
                    handleScan(scanInputValue);
                    setScanInputValue('');
                  }}
                  autoFocus
                  style={{ maxWidth: '500px' }}
                />
              </div>
            </div>
          </Card>

          {/* Item List */}
          <Card
            title={
              <Space>
                <InboxOutlined />
                <span>包裝項目</span>
                <Badge count={currentOrder.items.length} />
              </Space>
            }
          >
            <Table
              dataSource={currentOrder.items}
              rowClassName={(record) => {
                // 检查是否存在数量差异
                const hasDiscrepancy = record.expectedQuantity !== record.scannedQuantity || 
                                      record.expectedQuantity !== record.packedQuantity ||
                                      record.hasDiscrepancy;
                return hasDiscrepancy ? 'discrepancy-row' : '';
              }}
              columns={[
                {
                  title: 'SKU',
                  dataIndex: 'productSku',
                  key: 'productSku',
                  width: 120
                },
                {
                  title: '產品名稱',
                  dataIndex: 'productName',
                  key: 'productName'
                },
                {
                  title: '預期',
                  dataIndex: 'expectedQuantity',
                  key: 'expectedQuantity',
                  width: 60,
                  align: 'center'
                },
                {
                  title: '已掃描',
                  dataIndex: 'scannedQuantity',
                  key: 'scannedQuantity',
                  width: 80,
                  align: 'center',
                  render: (value, record) => (
                    <InputNumber
                      size="small"
                      min={0}
                      max={record.expectedQuantity}
                      value={value}
                      onChange={(newValue) => handleManualQuantityUpdate(record.id, 'scanned', newValue || 0)}
                      style={{ width: '60px' }}
                    />
                  )
                },
                {
                  title: '狀態',
                  dataIndex: 'status',
                  key: 'status',
                  width: 80,
                  render: (status, record) => {
                    if (record.hasDiscrepancy) {
                      return <Tag color="red">差異</Tag>;
                    }
                    const statusColors: Record<string, string> = {
                      'pending': 'default',
                      'scanned': 'blue'
                    };
                    const statusTexts: Record<string, string> = {
                      'pending': '待處理',
                      'scanned': '已掃描'
                    };
                    return <Tag color={statusColors[status]}>{statusTexts[status]}</Tag>;
                  }
                }
              ]}
              pagination={false}
              size="small"
              rowKey="id"
            />
          </Card>
        </Col>

        <Col span={8}>
          {/* Scan History */}
          <Card
            title={
              <Space>
                <ClockCircleOutlined />
                <span>掃描歷史</span>
                <Badge count={scanHistory.length} />
              </Space>
            }
            extra={
              <Tooltip title="清空掃描歷史">
                <Popconfirm
                  title="確認清空掃描歷史？"
                  okText="清空"
                  cancelText="取消"
                  onConfirm={handleClearScanHistory}
                >
                  <Button type="text" icon={<DeleteOutlined style={{ color: '#ff4d4f' }} />} />
                </Popconfirm>
              </Tooltip>
            }
          >
            <Timeline
              mode="left"
              style={{ maxHeight: '300px', overflowY: 'auto', wordBreak: 'break-word' }}
            >
              {scanHistory.map((scan, index) => (
                <Timeline.Item
                  key={index}
                  color={scan.success ? 'green' : 'red'}
                  dot={
                    scan.success ? 
                    <CheckCircleOutlined style={{ color: 'green' }} /> :
                    <ExclamationCircleOutlined style={{ color: 'red' }} />
                  }
                >
                  <div style={{ fontSize: '12px',padding: '2px' }}>
                    <div
                      style={{
                        fontWeight: 'bold',
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        overflowWrap: 'anywhere',
                        maxWidth: '100%'
                      }}
                    >
                      {scan.barcode}
                    </div>
                    <div style={{ color: '#666' }}>
                      {moment(scan.timestamp).format('HH:mm:ss')}
                    </div>
                    {!scan.success && (
                      <div
                        style={{
                          background: '#fff2f0',
                          border: '1px solid #ffccc7',
                          borderRadius: 4,
                          padding: 12,
                          marginTop: 4
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, color: '#cf1322' }}>
                          <ExclamationCircleOutlined style={{ color: '#cf1322' }} />
                          掃描失敗
                        </div>
                        <div style={{ marginTop: 6, color: '#cf1322' }}>
                          <span style={{ marginRight: 6 }}>❌</span>
                          {scan.error || '未知錯誤'}
                        </div>
                      </div>
                    )}
                    {scan.data && (
                      <div style={{ color: 'green', wordBreak: 'break-word', whiteSpace: 'normal', overflowWrap: 'anywhere' }}>
                        {scan.data.productName || '配貨單掃描成功'}
                      </div>
                    )}
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>
      </Row>

      {/* CSS Styles for discrepancy highlighting */}
      <style>{`
        .discrepancy-row {
          background-color: #fff2f0 !important;
          border-left: 4px solid #ff4d4f !important;
        }
        .discrepancy-row:hover {
          background-color: #ffccc7 !important;
        }
        .discrepancy-row td {
          border-color: #ffccc7 !important;
        }
      `}</style>
    </div>
  );
};

export default PackingScanInterface;